import { Button } from "@/components/ui/baseUi/button"
import { Calendar } from "@/components/ui/calendar"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { capitalize } from "@/modules/utils/stringUtils"
import type { FieldValueChange } from "@/typings/formik"
import dayjs from "dayjs"
import type { FieldInputProps } from "formik"
import { CalendarIcon } from "lucide-react"
import type { PropsBase as DayPickerPropsBase } from "react-day-picker"

type CalendarProps = {
  minDate: Date
  maxDate: Date
  dateFormat: string
  captionLayout?: DayPickerPropsBase["captionLayout"]
}

type CalendarInputProps<T, K> = {
  label?: string
  name: string
  isRequiredInput?: boolean
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<any>
  placeholder?: string
  error?: React.JSX.Element
  className?: string
  containerClassName?: string
  children?: React.ReactNode
  calendarProps?: CalendarProps
} & React.ComponentProps<"div">

export function CalendarInput<T, K>({
  label,
  name,
  isRequiredInput = false,
  setFieldValue,
  getFieldProps,
  placeholder,
  error,
  className,
  containerClassName,
  children,
  calendarProps = {
    minDate: new Date("1900-01-01"),
    maxDate: new Date("2060-01-01"),
    dateFormat: "PPP",
    captionLayout: "label",
  },
  ...props
}: CalendarInputProps<T, K>) {
  const value: Date = (getFieldProps(name as T).value as Date) || false

  return (
    <InputContainer
      label={label}
      name={name}
      isRequiredInput={isRequiredInput}
      error={error}
      className={className}
      {...props}
    >
      <div className={cn("inline-flex w-full cursor-pointer flex-col gap-2", containerClassName)}>
        <Popover>
          <PopoverTrigger
            render={
              <Button
                variant={"outline"}
                className={cn(
                  "flex h-11 w-full text-left font-normal whitespace-normal hover:border hover:border-borderPrimary hover:[&>svg]:text-primary-500 hover:[&>svg]:opacity-100",
                  !value && "text-textDefault-caption",
                )}
              >
                {value ? (
                  capitalize(dayjs(value).format(calendarProps.dateFormat))
                ) : (
                  <span>{placeholder || word("global.pickADate")}</span>
                )}
                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            }
          />
          <PopoverContent className="w-auto border-0 p-0" align="start">
            <Calendar
              mode="single"
              selected={value}
              captionLayout={calendarProps.captionLayout}
              disabled={(date) => date > calendarProps.maxDate || date < calendarProps.minDate}
              onSelect={function (date) {
                setFieldValue(name, date as Date)
              }}
            />
          </PopoverContent>
        </Popover>

        {children}
      </div>
    </InputContainer>
  )
}
